-- | Step 1 mass-update widgets for teacher and semester changes.
-- |
-- | The modals collect the target teacher or semester, and `updateFormsMutation`
-- | builds the backend mutation that the Step 1 runtime executes.
module Widgets.UpdateForm where

import Common

import API.RecentForms (RecentForm)
import API.Search (SearchSemester, SearchTeacher, semesterSearchAff, teacherSearchAff)
import API.Types (SemesterID, TeacherID, DocumentID)
import Concur.Core.Patterns (loopState)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Either (Either(..))
import Data.Maybe (isNothing)
import GraphQL.Query (Mutation, buildMutation, nameQuery)
import GraphQL.Type (NamedType, namedType)
import Widgets.BetterSelect (Select, defSelectConfig, initSelect', selectWidget, selectedItem)
import Widgets.Modal (ModalResult(..), modalWidget)


-- | We don't actually need this information but we must request at least one
-- | field from the mutation's return value.
type FormsResponseDummy = { forms :: Array { id :: DocumentID } }

type FormUpdateState a =
    { header :: String
    , forms :: Array RecentForm
    , select :: Select a
    }

type UpdateFormInput =
    { form_id :: DocumentID
    , semester :: Maybe SemesterID
    , teacher_id :: Maybe TeacherID
    }


semesterUpdateModal :: Array RecentForm -> Widget HTML (Maybe SearchSemester)
semesterUpdateModal forms = formUpdateModal
    { forms
    , header: lookupTranslation "update-semester-for" <> " " <> show count <> " " <> lookupTranslation "forms"
    , select: initSelect' "semester" semesterSearchAff $ defSelectConfig
        { placeholder = Just $ lookupTranslation "search-semester"
        , allowDeselect = false
        }
    }
  where
    count :: Int
    count = length forms

teacherUpdateModal :: Array RecentForm -> Widget HTML (Maybe SearchTeacher)
teacherUpdateModal forms = formUpdateModal
    { forms
    , header: lookupTranslation "update-teacher-for" <> " " <> show count <> " " <> lookupTranslation "forms"
    , select: initSelect' "teacher" teacherSearchAff $ defSelectConfig
        { placeholder = Just $ lookupTranslation "search-teacher"
        , allowDeselect = false
        }
    }
  where
    count :: Int
    count = length forms

updateFormsMutation :: Either SearchSemester SearchTeacher -> Array RecentForm -> Mutation FormsResponseDummy
updateFormsMutation selection forms = case selection of
    Left sem -> nameQuery "MassUpdateSemesters" $ buildMutation "updateForms"
        { input: map (semesterInput sem) forms }
    Right teacher -> nameQuery "MassUpdateTeachers" $ buildMutation "updateForms"
        { input: map (teacherInput teacher) forms }
  where
    semesterInput :: SearchSemester -> RecentForm -> NamedType "UpdateFormInput" UpdateFormInput
    semesterInput sem f = namedType
        { form_id: f.id
        , semester: Just sem.semester
        , teacher_id: Nothing
        }

    teacherInput :: SearchTeacher -> RecentForm -> NamedType "UpdateFormInput" UpdateFormInput
    teacherInput teacher f = namedType
        { form_id: f.id
        , semester: Nothing
        , teacher_id: Just teacher.id
        }



formUpdateModal :: forall a. FormUpdateState a -> Widget HTML (Maybe a)
formUpdateModal state = do
    selected <- finalSelection <$> (modalWidget (D.div' []) $ formUpdateSelect state)
    pure selected
  where
    finalSelection :: ModalResult (FormUpdateState a) -> Maybe a
    finalSelection ModalClosed = Nothing
    finalSelection (ModalReturned st) = _.value <$> selectedItem st.select

formUpdateSelect :: forall a. FormUpdateState a -> Widget HTML (FormUpdateState a)
formUpdateSelect state' = loopState state' \state -> D.div
    [ P.className "modal-col modal-narrow" ]
    [ D.h1 [ P.className "modal-heading" ] [ D.text state.header ]
    , Left <$> state { select = _ } <$> selectWidget state.select
    , D.button
        [ P.className "btn btn-primary"
        , P.disabled $ isNothing $ selectedItem state.select
        , Right state <$ P.onClick
        ]
        [ D.text $ lookupTranslation "save" ]
    ]
